import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import { useNavigate } from "react-router-dom";

// ✅ use OTP cookie-auth service (withCredentials)
import { otpBorrowerLoansMe } from "../../service/otpApi";
import CommunicationPreferences from "./CommunicationPreferences";

const BorrowerLoans = () => {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [prefsOpen, setPrefsOpen] = useState(false);
  const navigate = useNavigate();

  const fetchLoans = async () => {
    try {
      setLoading(true);
      setErrorMsg("");

      // ✅ cookie-based auth (otpApi should send withCredentials)
      const response = await otpBorrowerLoansMe();

      if (response?.status === 200) {
        setLoans(response.data?.loans || []);
      } else {
        setErrorMsg(response?.data?.message || "Failed to load loans.");
      }
    } catch (error) {
      const msg = error?.response?.data?.message;
      const status = error?.response?.status;

      // ✅ if not authenticated, bounce to login
      if (status === 401) {
        navigate("/borrower/login", { replace: true });
        return;
      }

      console.error("Error fetching borrower loans:", error);
      setErrorMsg(msg || "An unexpected error occurred while loading your loans.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLoans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoanClick = (loan) => {
    navigate(`/borrower/loan/${loan.loanId}/tasks`, { state: { loan } });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1000, mx: "auto" }}>
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{ fontWeight: 800, textAlign: "center", color: "text.primary" }}
        >
          My Loans
        </Typography>
        <Tooltip title="Communication Preferences">
          <IconButton onClick={() => setPrefsOpen(true)} sx={{ ml: 1.5 }}>
            <SettingsIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress />
        </Box>
      ) : errorMsg ? (
        <Alert severity="error" sx={{ mt: 4, textAlign: "center" }}>
          {errorMsg}
        </Alert>
      ) : loans.length === 0 ? (
        <Typography textAlign="center" color="text.secondary">
          You currently have no active loans associated with your email.
        </Typography>
      ) : (
        <Grid container spacing={3} direction="column" alignItems="stretch">
          {loans.map((loan) => (
            <Grid item xs={12} key={loan.loanId}>
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: 4,
                  width: "100%",
                  transition: "transform 0.25s ease, box-shadow 0.25s ease",
                  "&:hover": {
                    boxShadow: 10,
                    transform: "translateY(-6px)",
                    cursor: "pointer",
                  },
                }}
                onClick={() => handleLoanClick(loan)}
              >
                <CardActionArea>
                  <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                    <Typography variant="h5" fontWeight={700} gutterBottom>
                      Loan #{loan.loanNumber}
                    </Typography>

                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                      Borrower: {loan.borrowerName}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body1" fontWeight={600}>
                        Property Address
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {loan.streetAddress
                          ? `${loan.streetAddress}, ${loan.streetCity}, ${loan.state} ${loan.zip}`
                          : "Address not available"}
                      </Typography>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Purpose:</strong> {loan.loanPurpose || "—"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Status:</strong> {loan.loanStatus || "In Progress"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Amount:</strong>{" "}
                          {loan.loanAmount ? `$${Number(loan.loanAmount).toLocaleString()}` : "—"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Last Modified:</strong> {loan.lastModified || "—"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <CommunicationPreferences open={prefsOpen} onClose={() => setPrefsOpen(false)} />
    </Box>
  );
};

export default BorrowerLoans;
